<template>
  <div>
    <div id="diagramdiv">
      <canvas id="diagram-canvas"></canvas>
      <canvas id="minimap-canvas" height="200" width="200"></canvas>
    </div>
    <div style="display: none;">
      <img id="canvas_logo" :src="require('@/assets/logo_v2_black.png')"/>
    </div>
  </div>
</template>

<script>
import interact from 'interactjs'
import { mapMutations, mapActions, mapState } from 'vuex'
import canvasConstants from '@/store/modules/canvas/canvasConstants'

function inDOMElement (id, x, y) {
  const element = document.getElementById(id)
  const bounds = element.getBoundingClientRect()
  return (bounds.left <= x) && (x <= bounds.right) &&
         (bounds.top <= x) && (x <= bounds.bottom)
}

export default {
  name: 'Diagram',
  methods: {
    ...mapMutations('canvas', ['setCanvas', 'setDnDOverDiagram']),
    ...mapActions('canvas', [
      'onMouseWheel', 'onMouseMove', 'onMouseDown', 'onMouseUp',
      'onTouchStart', 'onTouchEnd', 'onTouchMove',
      'centerCanvas', 'drawCanvas',
      'createNodeFromHMW', 'createNodeFromThought', 'createNodeFromFact'
    ])
  },

  computed: {
    ...mapState({
      currentlyViewedChallenge: state => state.challenge.currentlyViewedChallenge
    }),
    ...mapState('canvas', ['dndOverDiagram'])
  },
  mounted () {
    const self = this

    const diagram = document.getElementById('diagram-canvas')
    const diagramCtx = diagram.getContext('2d', { alpha: false })
    const minimap = document.getElementById('minimap-canvas')
    const minimapCtx = minimap.getContext('2d', { alpha: false })
    this.setCanvas({
      diagramCtx: diagramCtx,
      minimapCtx: minimapCtx
    })

    // Mouse interaction handling
    diagram.onwheel = function (evt) {
      const isMouse = evt.deltaY !== -evt.wheelDeltaY
      self.onMouseWheel(isMouse ? evt.deltaY / 10 : evt.deltaY)
    }

    if (!self.currentlyViewedChallenge.archived) {
      diagram.onmousemove = function (evt) {
        self.onMouseMove({ x: evt.offsetX, y: evt.offsetY })
      }

      diagram.onmousedown = function (evt) {
        self.onMouseDown()
      }

      document.onmouseup = function (evt) {
        self.onMouseUp()
      }

      // Touch interaction handling
      diagram.addEventListener('touchstart', function (evt) {
        evt.preventDefault()
        const parentRect = evt.target.getBoundingClientRect()

        const touches = []
        evt.touches.forEach(touch => {
          touches.push({
            x: touch.clientX - parentRect.x,
            y: touch.clientY - parentRect.y
          })
        })

        self.onTouchStart(touches)
      }, false)

      diagram.addEventListener('touchend', function (evt) {
        evt.preventDefault()
        self.onTouchEnd()
      }, false)

      diagram.addEventListener('touchmove', function (evt) {
        evt.preventDefault()
        const parentRect = evt.target.getBoundingClientRect()

        const touches = []
        evt.touches.forEach(touch => {
          touches.push({
            x: touch.clientX - parentRect.x,
            y: touch.clientY - parentRect.y
          })
        })

        self.onTouchMove(touches)
      }, false)

      interact('#diagramdiv').dropzone({
        // Require a 75% element overlap for a drop to be possible
        overlap: 0.75,

        // listen for drop related events:
        ondragenter: function (event) {
          self.setDnDOverDiagram(true)
        },
        ondragleave: function (event) {
          self.setDnDOverDiagram(false)
        }
      })

      const thoughtDragMove = function (event) {
        var target = event.target
        // keep the dragged position in the data-x/data-y attributes
        var x = (parseFloat(target.getAttribute('data-x')) || 0) + event.dx
        var y = (parseFloat(target.getAttribute('data-y')) || 0) + event.dy

        // translate the element
        target.style.webkitTransform =
          target.style.transform =
            'translate(' + x + 'px, ' + y + 'px)'

        // update the posiion attributes
        target.setAttribute('data-x', x)
        target.setAttribute('data-y', y)
      }

      const thoughtDragStart = function (event) {}

      const thoughtDragEnd = function (event) {
        var target = event.target

        target.style.webkitTransform =
          target.style.transform =
            'translate(0, 0)'

        target.setAttribute('data-x', 0)
        target.setAttribute('data-y', 0)

        const inThoughtCatcher = inDOMElement('thoughtCatcher', event.client.x, event.client.y)
        const inParkingLot = inDOMElement('parkingLot', event.client.x, event.client.y)
        const inFacts = inDOMElement('facts', event.client.x, event.client.y)

        if (self.dndOverDiagram && !inThoughtCatcher && !inParkingLot && !inFacts) {
          const canvasBounds = diagram.getBoundingClientRect()
          const tf = diagramCtx.getTransform().invertSelf()
          const mX = event.clientX - canvasBounds.left
          const mY = event.clientY - canvasBounds.top

          const pos = {
            x: tf.a * mX + tf.c * mY + tf.e,
            y: tf.b * mX + tf.d * mY + tf.f
          }

          if (target.dataset.thoughttype === 'HMW') {
            self.createNodeFromHMW({
              x: pos.x - 100,
              y: pos.y - 50,
              HMWID: target.dataset.thoughtid,
              width: canvasConstants.defaultNodeWidth,
              height: canvasConstants.defaultNodeHeight,
              fill: self.$store.state.challenge.currentlyViewedChallenge.defaultNodeColor,
              font: canvasConstants.defaultNodeFont,
              fontColor: self.$store.state.challenge.currentlyViewedChallenge.defaultFontColor,
              dotColor: self.$store.state.challenge.currentlyViewedChallenge.defaultDotColor
            })
          } else if (target.dataset.thoughttype === 'Fact') {
            self.createNodeFromFact({
              x: pos.x - 100,
              y: pos.y - 50,
              factID: target.dataset.thoughtid,
              width: canvasConstants.defaultNodeWidth,
              height: canvasConstants.defaultNodeHeight,
              fill: self.$store.state.challenge.currentlyViewedChallenge.defaultNodeColor,
              font: canvasConstants.defaultNodeFont,
              fontColor: self.$store.state.challenge.currentlyViewedChallenge.defaultFontColor,
              dotColor: self.$store.state.challenge.currentlyViewedChallenge.defaultDotColor
            })
          } else if (target.dataset.thoughttype === 'publicThought') {
            self.createNodeFromThought({
              x: pos.x - 100,
              y: pos.y - 50,
              thoughtID: target.dataset.thoughtid,
              width: canvasConstants.defaultNodeWidth,
              height: canvasConstants.defaultNodeHeight,
              fill: self.$store.state.challenge.currentlyViewedChallenge.defaultNodeColor,
              font: canvasConstants.defaultNodeFont,
              fontColor: self.$store.state.challenge.currentlyViewedChallenge.defaultFontColor,
              dotColor: self.$store.state.challenge.currentlyViewedChallenge.defaultDotColor
            })
          }
        }

        self.setDnDOverDiagram(false)
      }

      interact('.drag-drop').draggable({
        listeners: {
          move: thoughtDragMove,
          start: thoughtDragStart,
          end: thoughtDragEnd
        }
      })
    }

    const onResize = function (redrawCanvas) {
      console.log('ON RESIZE: centerCanvas:', redrawCanvas)

      const outerCanvasContainer = document.getElementById('diagramdiv')
      if (outerCanvasContainer) {
        const containerWidth = self.currentlyViewedChallenge.isFacilitator || self.currentlyViewedChallenge.isCofacilitator ? (outerCanvasContainer.clientWidth - 100) : outerCanvasContainer.clientWidth
        const containerHeight = outerCanvasContainer.clientHeight

        diagram.width = containerWidth
        diagram.height = containerHeight
        minimap.style.left = `${containerWidth - minimap.height - 10}px`

        if (redrawCanvas) {
          self.centerCanvas()
        } else {
          self.drawCanvas()
        }
      }
    }

    window.addEventListener('resize', () => onResize(false))
    document.getElementById('canvas_logo').addEventListener('load', () => {
      console.log('CANVAS LOGO LOADED')
      onResize(true)
    })
    onResize(false)
  }
}
</script>

<style lang="sass">
  #diagramdiv
    width: 100%
    // navbar height
    height: calc(100vh - 60px)
    position: absolute
    top: 0
    left: 0
    z-index: 0

  #minimap-canvas
    position: absolute
    border: solid black
    top: 10px
    border-radius: 5px
    box-shadow: 0 0 5px 0
</style>
